// src/theme.ts — "Light Heritage" design tokens, ported verbatim from the
// prototype bp-theme.jsx (DESIGN-REFERENCE §2). The prototype wins visual
// disputes — copy values, don't invent. No CSS framework; components consume
// these via inline style objects (NYMR convention).

export const C = {
  paper:     "#FBF6E8",
  paperWarm: "#F5EBD3",
  paperSoft: "#FFFDF6",
  card:      "#FFFFFF",

  ink:       "#2B2014",
  inkSoft:   "#604C32",
  inkMute:   "#9F8B6B",
  inkFaint:  "#C2B391",
  inkOnDark: "#FBF4DF",

  brass:     "#A6771C",
  brassDk:   "#7A540B",
  brassLt:   "#D9B466",
  brassGlow: "#F4E2A8",
  brassBg:   "#FBEFCB",
  brassSurf: "#FFF6DC",

  heather:   "#7A5D8C",
  heatherDk: "#56406A",
  sage:      "#6E8C72",
  sageDk:    "#4F6852",
  signal:    "#B83A2B",
  signalDk:  "#8A2A1F",
  signalBg:  "#F8D9D2",

  hairline:   "#E8DCBC",
  hairlineLt: "#F2EAD3",
  rule:       "#EFE6CE",

  shadowS: "0 1px 0 rgba(122,84,11,0.04), 0 2px 6px rgba(122,84,11,0.06)",
  shadowM: "0 1px 0 rgba(122,84,11,0.04), 0 8px 22px rgba(122,84,11,0.10)",
  shadowL: "0 2px 0 rgba(122,84,11,0.05), 0 22px 50px rgba(122,84,11,0.16)",
  shadowXL: "0 30px 80px rgba(43,32,20,0.22), 0 6px 18px rgba(43,32,20,0.10)",
} as const;

export const F = {
  serif: "'Playfair Display','Times New Roman',serif",
  body:  "'Nunito','Segoe UI',sans-serif",
  mono:  "'IBM Plex Mono','Courier New',monospace",
} as const;

export interface Dept {
  id: string;
  name: string;
  short: string;
  fg: string;
  bg: string;
  bd: string;
  focus?: boolean;
}

// Six focus depts get vivid heritage-harmonious hues; the rest sit in muted ink.
export const DEPTS: Dept[] = [
  { id: "operating", name: "Operating",         short: "OPS",   fg: "#2B5A66", bg: "#DCEAEC", bd: "rgba(43,90,102,0.24)", focus: true },
  { id: "mpd",       name: "MPD (Motive Power)", short: "MPD",   fg: "#7A540B", bg: "#FBEFCB", bd: "rgba(167,117,28,0.26)", focus: true },
  { id: "cw",        name: "Carriage & Wagon",   short: "C&W",   fg: "#56406A", bg: "#ECE0F2", bd: "rgba(122,93,140,0.26)", focus: true },
  { id: "st",        name: "Signal & Telecoms",  short: "S&T",   fg: "#8A2A1F", bg: "#F8DBD4", bd: "rgba(184,58,43,0.26)",  focus: true },
  { id: "pway",      name: "Permanent Way",      short: "P-WAY", fg: "#4F6852", bg: "#E4ECDF", bd: "rgba(110,140,114,0.26)", focus: true },
  { id: "catering",  name: "Catering & Retail",  short: "CAT",   fg: "#92384F", bg: "#F6DDE3", bd: "rgba(146,56,79,0.24)",  focus: true },
  { id: "pullman",   name: "Pullman Dining",     short: "PULL",  fg: "#7A4E2A", bg: "#F1E4D1", bd: "rgba(122,78,42,0.22)" },
  { id: "events",    name: "Events",             short: "EVT",   fg: "#6A4A86", bg: "#EAE2F4", bd: "rgba(106,74,134,0.22)" },
  { id: "lineside",  name: "Lineside & Cons.",   short: "LINE",  fg: "#5E6E3F", bg: "#EAEBD7", bd: "rgba(94,110,63,0.22)" },
  { id: "retail",    name: "Retail",             short: "RTL",   fg: "#9A5A1F", bg: "#F7E6CF", bd: "rgba(154,90,31,0.22)" },
  { id: "people",    name: "People",             short: "PPL",   fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
  { id: "estates",   name: "Estates",            short: "EST",   fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
  { id: "marketing", name: "Marketing",          short: "MKT",   fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
  { id: "structures", name: "Structures",        short: "STR",   fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
  { id: "it",        name: "IT",                 short: "IT",    fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
  { id: "hs",        name: "Health & Safety",    short: "H&S",   fg: "#604C32", bg: "#F1E9D6", bd: "rgba(96,76,50,0.20)" },
];

export const DEPT_MAP: Record<string, Dept> = Object.fromEntries(DEPTS.map((d) => [d.id, d]));

export interface TtType { label: string; fg: string; bg: string; dot: string; }

// Matches the spreadsheet "Key" (Red / Blue / Pink T/T, Diner only) + named seasonal services.
export const TT_TYPES: Record<string, TtType> = {
  red:     { label: "Red Timetable",  fg: "#8A2A1F", bg: "#F6D9D2", dot: "#B83A2B" },
  blue:    { label: "Blue Timetable", fg: "#2B5A66", bg: "#D8E8EB", dot: "#2B7A8C" },
  pink:    { label: "Pink Timetable", fg: "#8A3A66", bg: "#F4DCEA", dot: "#C25F94" },
  diner:   { label: "Diner Only",     fg: "#7A540B", bg: "#F6E6C4", dot: "#A6771C" },
  evening: { label: "Evening Diner",  fg: "#56406A", bg: "#ECE0F2", dot: "#7A5D8C" },
  event:   { label: "Special Event",  fg: "#6A4A86", bg: "#EAE2F4", dot: "#7A5D8C" },
  thomas:  { label: "Thomas",         fg: "#2B5A66", bg: "#DCEAEC", dot: "#2B7A8C" },
  gala:    { label: "Gala",           fg: "#8A2A1F", bg: "#F6D9D2", dot: "#B83A2B" },
  winter:  { label: "Winter Service", fg: "#4F6852", bg: "#E4ECDF", dot: "#6E8C72" },
  prep:    { label: "Prep Day",       fg: "#9A5A1F", bg: "#F7E6CF", dot: "#C98A3A" },
  none:    { label: "No Services",    fg: "#9F8B6B", bg: "#F1E9D6", dot: "#C2B391" },
};

export interface SetDef { id: string; name: string; short: string; kind: string; }
export const SETS: SetDef[] = [
  { id: "w1",    name: "Whitby Set 1",    short: "WS1", kind: "set" },
  { id: "w2",    name: "Whitby Set 2",    short: "WS2", kind: "set" },
  { id: "i1",    name: "Internal Set 1",  short: "IS1", kind: "set" },
  { id: "i2",    name: "Internal Set 2",  short: "IS2", kind: "set" },
  { id: "diner", name: "Moorlander Diner", short: "DIN", kind: "diner" },
];

export interface LocoReq { id: string; name: string; short: string; traction: string; }
export const LOCO_REQ: LocoReq[] = [
  { id: "ws",  name: "Whitby Steam",    short: "W·Steam",  traction: "steam" },
  { id: "is",  name: "Internal Steam",  short: "I·Steam",  traction: "steam" },
  { id: "wd",  name: "Whitby Diesel",   short: "W·Diesel", traction: "diesel" },
  { id: "id",  name: "Internal Diesel", short: "I·Diesel", traction: "diesel" },
  { id: "dnr", name: "Diner Loco",      short: "Diner",    traction: "diesel" },
];
